import { format } from "date-fns";
import { es } from "date-fns/locale";
import { formatLaborDurationMinutes, resolveLaborDayMinutes } from "../../../shared/laborDuration";
import { resolveLaborInstallationIdentity } from "../../../shared/laborIdentity";

/**
 * Deterministic color palette for calendar installations.
 * Provides distinct, accessible colors across planning and execution modes.
 */
export const INSTALLATION_COLOR_PALETTE = [
  "#2563eb", // blue-600
  "#7c3aed", // violet-600
  "#0d9488", // teal-600
  "#c026d3", // fuchsia-600
  "#d97706", // amber-600
  "#0284c7", // sky-600
  "#4f46e5", // indigo-600
  "#059669", // emerald-600
  "#db2777", // pink-600
  "#65a30d", // lime-600
  "#ea580c", // orange-600
  "#475569", // slate-600
] as const;

export const DEFAULT_INSTALLATION_COLOR = "#4b5563"; // gray-600

/**
 * Returns a stable, deterministic hex color based on the installation ID.
 * Identical installation IDs will always yield the exact same color in both planning and execution.
 */
export function getInstallationColor(installationId: unknown): string {
  const id = Number(installationId);
  if (Number.isInteger(id) && id > 0) {
    const index = (id - 1) % INSTALLATION_COLOR_PALETTE.length;
    return INSTALLATION_COLOR_PALETTE[index];
  }
  return DEFAULT_INSTALLATION_COLOR;
}

export function isContractualPlanningCommitment(installation: {
  contractId?: unknown;
  contractualEndDate?: unknown;
  contractDatesFixed?: unknown;
} | null | undefined): boolean {
  if (installation?.contractId !== null && installation?.contractId !== undefined) {
    return Boolean(installation.contractualEndDate);
  }
  return installation?.contractDatesFixed === true;
}

export function getPlanningEventStyle(installationColor: string, isContractual: boolean) {
  return {
    backgroundColor: installationColor,
    ...(isContractual ? { border: "1px solid #dc2626" } : {}),
    borderLeft: `4px solid ${installationColor}`,
  };
}

export function formatContractDeadlineDetails(installation: {
  contractId?: unknown;
  contractCode?: unknown;
  contractualEndDate?: unknown;
} | null | undefined): string | null {
  const date = typeof installation?.contractualEndDate === "string"
    ? installation.contractualEndDate.split("T")[0]
    : "";
  if (!installation?.contractId || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const [year, month, day] = date.split("-");
  const code = typeof installation.contractCode === "string" && installation.contractCode.trim()
    ? installation.contractCode.trim()
    : `#${installation.contractId}`;
  return `⚠ Compromiso contractual\nContrato: ${code}\nFecha límite: ${day}/${month}/${year}`;
}

export function getCalendarEventStyle(event: any) {
  const resourceStyle = event?.resource?.style ?? {};
  return {
    backgroundColor: resourceStyle.backgroundColor || "#4b5563",
    borderRadius: "4px",
    opacity: 0.95,
    color: "white",
    border: resourceStyle.border || "0px",
    borderLeft: resourceStyle.borderLeft || "none",
    display: "block",
    cursor: "pointer",
  };
}

export type CalendarStatusPresentation = {
  label: string;
  dotColor: string;
  badgeClass: string;
};

/**
 * Spanish status presentation for planning mode installations.
 */
export function getPlanningStatusPresentation(
  status: string | null | undefined
): CalendarStatusPresentation {
  switch (status) {
    case "pending":
      return {
        label: "Pendiente",
        dotColor: "#f59e0b",
        badgeClass: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300",
      };
    case "in_progress":
      return {
        label: "Iniciada",
        dotColor: "#3b82f6",
        badgeClass: "bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950 dark:text-blue-300",
      };
    case "completed":
      return {
        label: "Completada",
        dotColor: "#10b981",
        badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300",
      };
    case "cancelled":
      return {
        label: "Cancelada",
        dotColor: "#ef4444",
        badgeClass: "bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950 dark:text-rose-300",
      };
    default:
      return {
        label: status || "Sin estado",
        dotColor: "#6b7280",
        badgeClass: "bg-gray-50 text-gray-700 border-gray-300 dark:bg-gray-900 dark:text-gray-300",
      };
  }
}

/**
 * Spanish status presentation for execution mode calendar events.
 */
export function getExecutionStatusPresentation(
  status: string | null | undefined
): CalendarStatusPresentation {
  switch (status) {
    case "assigned":
      return {
        label: "Asignado",
        dotColor: "#9ca3af",
        badgeClass: "bg-slate-50 text-slate-700 border-slate-300 dark:bg-slate-900 dark:text-slate-300",
      };
    case "working":
      return {
        label: "Trabajando",
        dotColor: "#3b82f6",
        badgeClass: "bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950 dark:text-blue-300",
      };
    case "paused":
      return {
        label: "Pausado",
        dotColor: "#f59e0b",
        badgeClass: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300",
      };
    case "completed":
    case "jornada_terminada":
      return {
        label: "Jornada terminada",
        dotColor: "#10b981",
        badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300",
      };
    default:
      return {
        label: status || "Sin estado",
        dotColor: "#6b7280",
        badgeClass: "bg-gray-50 text-gray-700 border-gray-300 dark:bg-gray-900 dark:text-gray-300",
      };
  }
}

/**
 * Dispatches status presentation based on the calendar mode.
 * "laboral" (and its legacy alias "execution") always resolves to workday/assignment
 * semantics, never to installation status semantics.
 */
export function getStatusPresentation(
  mode: "planning" | "laboral" | "execution",
  status: string | null | undefined
): CalendarStatusPresentation {
  return mode === "planning"
    ? getPlanningStatusPresentation(status)
    : getExecutionStatusPresentation(status);
}

// ========== CAL-08: Three unambiguous calendar views ==========

/**
 * Canonical calendar views:
 * - "planning": theoretical dates and planning status (behavior preserved).
 * - "real": explicit execution evidence per installation (realStartDate/realEndDate).
 * - "laboral": technician daily assignments/workdays (legacy "execution" view).
 */
export type CalendarViewMode = "planning" | "real" | "laboral";

/**
 * Parses the `view` URL query param into a canonical view mode.
 * Legacy links remain compatible: `view=execution` maps to "laboral".
 * Unknown/missing values fall back to "planning".
 */
export function parseCalendarViewParam(param: string | null | undefined): CalendarViewMode {
  if (param === "real") return "real";
  if (param === "laboral" || param === "execution") return "laboral";
  return "planning";
}

/**
 * Canonical value written back to the `view` URL query param.
 */
export function getCalendarViewParam(mode: CalendarViewMode): string {
  return mode;
}

/**
 * Parses a timestamp/date value ("YYYY-MM-DD...", Date) into a local Date at 00:00
 * of the calendar day, or null when the value holds no explicit evidence.
 */
export function parseCalendarDateOnly(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value !== "string") return null;
  const parts = value.split("T")[0].split("-").map(Number);
  if (parts.length < 3 || parts.some((p) => !Number.isInteger(p))) return null;
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Parses a timestamp value into a full Date (date + time) or null when invalid.
 */
export function parseCalendarTimestamp(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== "string" || !value.trim()) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return parseCalendarDateOnly(value);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Formats an explicit real timestamp (e.g. realStartDate) for truthful display.
 */
export function formatRealTimestampLabel(value: unknown): string | null {
  const date = parseCalendarTimestamp(value);
  if (!date) return null;
  return format(date, typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? "d MMM yyyy" : "d MMM yyyy, HH:mm", { locale: es });
}

/**
 * True only when the installation carries explicit real execution evidence
 * (realStartDate set by the system on first startWork). Planning dates and
 * workday states are never treated as real evidence.
 */
export function hasRealExecutionEvidence(installation: {
  realStartDate?: unknown;
} | null | undefined): boolean {
  return parseCalendarTimestamp(installation?.realStartDate) !== null;
}

export const REAL_COMPLETION_WITHOUT_VERIFIED_CLOSE_LABEL = "Completada · fecha real de cierre no acreditada";
export const REAL_PHASE_WITHOUT_VERIFIED_CLOSE_LABEL = "Fin real de fase no acreditado · instalación completada";

/**
 * Physical close is derived only from explicit real dates. Installation status
 * may qualify a missing close as administratively completed, but never supplies
 * a date or converts it to verified physical evidence. Workday states remain
 * separate ("Jornada terminada", "Trabajando", "Pausado", "Asignado").
 */
export function getRealStatusPresentation(evidence: {
  realStartDate?: unknown;
  realEndDate?: unknown;
  installationStatus?: unknown;
  scope?: "installation" | "phase";
} | null | undefined): CalendarStatusPresentation {
  const hasStart = parseCalendarTimestamp(evidence?.realStartDate) !== null;
  const hasEnd = parseCalendarTimestamp(evidence?.realEndDate) !== null;
  if (hasStart && hasEnd) {
    return {
      label: "Finalizada",
      dotColor: "#10b981",
      badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300",
    };
  }
  if (hasStart && evidence?.installationStatus === "completed") {
    return {
      label: evidence.scope === "phase"
        ? REAL_PHASE_WITHOUT_VERIFIED_CLOSE_LABEL
        : REAL_COMPLETION_WITHOUT_VERIFIED_CLOSE_LABEL,
      dotColor: "#6b7280",
      badgeClass: "bg-gray-50 text-gray-700 border-gray-300 dark:bg-gray-900 dark:text-gray-300",
    };
  }
  if (hasStart) {
    return {
      label: "En curso",
      dotColor: "#3b82f6",
      badgeClass: "bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950 dark:text-blue-300",
    };
  }
  return {
    label: "Sin evidencia real",
    dotColor: "#6b7280",
    badgeClass: "bg-gray-50 text-gray-700 border-gray-300 dark:bg-gray-900 dark:text-gray-300",
  };
}

/**
 * CAL-08 semantic gap states, kept deliberately distinct:
 * - REAL_GAP_NO_ACTIVITY_LABEL: no explicit real activity evidence exists
 *   (no activity rows with technician/minutes for this installation).
 * - REAL_GAP_NO_SESSION_PHASE_LINK_LABEL: legacy real activity exists without
 *   an explicit phase link; do not attribute it by planned dates.
 * Neither label implies absence of activity when activity is proven.
 */
export const REAL_GAP_NO_ACTIVITY_LABEL = "Sin actividad real";
export const REAL_GAP_NO_SESSION_PHASE_LINK_LABEL = "Sin sesión asociada a fase";

export type RealSessionGapLabel =
  | typeof REAL_GAP_NO_ACTIVITY_LABEL
  | typeof REAL_GAP_NO_SESSION_PHASE_LINK_LABEL;

/**
 * Resolves the truthful gap label from the installation's real activity
 * aggregate. Activity evidence (any technician name or explicit nonnegative recorded
 * minutes) must never be presented as "no real session/activity".
 */
export function resolveRealSessionGapLabel(
  activity: RealActivityAggregate | null | undefined
): RealSessionGapLabel {
  const hasActivityEvidence =
    (activity?.technicians?.length ?? 0) > 0 ||
    (typeof activity?.totalMinutes === "number" && Number.isFinite(activity.totalMinutes) && activity.totalMinutes >= 0);
  return hasActivityEvidence
    ? REAL_GAP_NO_SESSION_PHASE_LINK_LABEL
    : REAL_GAP_NO_ACTIVITY_LABEL;
}

/**
 * Real activity rows are executionCalendar rows that go beyond a mere
 * "assigned" workday state (working / paused / completed): they carry explicit
 * recorded activity (technician, minutes). Mere assignments are excluded.
 */
export function isRealActivityRow(row: { status?: unknown } | null | undefined): boolean {
  return row != null && row.status !== "assigned";
}

export type RealActivityAggregate = {
  technicians: string[];
  totalMinutes: number | null;
};

/**
 * Aggregates real activity rows per installation: unique technician names and
 * total recorded real minutes. Used only as supplementary evidence for Real
 * events; never as a source of real dates.
 */
export function aggregateRealActivityByInstallation(
  rows: Array<Record<string, unknown>> | null | undefined
): Map<number, RealActivityAggregate> {
  const aggregate = new Map<number, RealActivityAggregate>();
  if (!Array.isArray(rows)) return aggregate;

  rows.forEach((row) => {
    if (!isRealActivityRow(row)) return;
    const installationId = Number(row.installationId);
    if (!Number.isInteger(installationId) || installationId <= 0) return;

    const entry = aggregate.get(installationId) ?? { technicians: [], totalMinutes: null };
    const technicianName = typeof row.technicianName === "string" ? row.technicianName.trim() : "";
    if (technicianName && !entry.technicians.includes(technicianName)) {
      entry.technicians.push(technicianName);
    }
    // Explicit zero is recorded evidence, unlike null/undefined/blank values.
    // Preserve numeric-string compatibility without Number(null/false/"") inventing zero.
    const rawMinutes = row.totalMinutes;
    const minutes = typeof rawMinutes === "number"
      ? rawMinutes
      : typeof rawMinutes === "string" && rawMinutes.trim() !== ""
        ? Number(rawMinutes)
        : Number.NaN;
    if (Number.isFinite(minutes) && minutes >= 0) {
      entry.totalMinutes = (entry.totalMinutes ?? 0) + minutes;
    }
    aggregate.set(installationId, entry);
  });

  return aggregate;
}

export type RealVsPlanningDiff = {
  hasPlannedDates: boolean;
  plannedStart: string | null;
  plannedEnd: string | null;
  /** real start − planned start, in calendar days. Positive = started later. */
  startDeltaDays: number | null;
  /** real end − planned end, in calendar days. Null while still in progress. */
  endDeltaDays: number | null;
};

const DAY_MS = 86400000;

/**
 * Transparent day-level difference between explicit real dates and the
 * planned segment range (earliest segment start / latest segment end).
 * Only computed when planned dates actually exist.
 */
export function computeRealVsPlanningDiff(input: {
  planningSegments?: Array<{ startDate?: unknown; endDate?: unknown }> | null;
  realStartDate?: unknown;
  realEndDate?: unknown;
}): RealVsPlanningDiff {
  let plannedStart: Date | null = null;
  let plannedEnd: Date | null = null;

  const segments = input.planningSegments ?? [];
  for (const seg of segments) {
    const segStart = parseCalendarDateOnly(seg.startDate);
    const segEnd = parseCalendarDateOnly(seg.endDate);
    if (segStart && (!plannedStart || segStart < plannedStart)) plannedStart = segStart;
    if (segEnd && (!plannedEnd || segEnd > plannedEnd)) plannedEnd = segEnd;
  }

  const realStart = parseCalendarDateOnly(input.realStartDate);
  const realEnd = parseCalendarDateOnly(input.realEndDate);
  const hasPlannedDates = plannedStart !== null || plannedEnd !== null;

  const startDeltaDays =
    hasPlannedDates && plannedStart !== null && realStart !== null
      ? Math.round((realStart.getTime() - plannedStart.getTime()) / DAY_MS)
      : null;
  const endDeltaDays =
    hasPlannedDates && plannedEnd !== null && realEnd !== null
      ? Math.round((realEnd.getTime() - plannedEnd.getTime()) / DAY_MS)
      : null;

  return {
    hasPlannedDates,
    plannedStart: plannedStart !== null && hasPlannedDates ? format(plannedStart, "d MMM yyyy", { locale: es }) : null,
    plannedEnd: plannedEnd !== null && hasPlannedDates ? format(plannedEnd, "d MMM yyyy", { locale: es }) : null,
    startDeltaDays,
    endDeltaDays,
  };
}

/**
 * Human-readable, transparent summary of the real vs planned difference.
 * Returns null when there are no planned dates to compare against.
 */
export function formatRealVsPlanningDiffSummary(diff: RealVsPlanningDiff | null | undefined): string | null {
  if (!diff?.hasPlannedDates) return null;
  const parts: string[] = [];
  const describe = (delta: number | null): string | null => {
    if (delta === null || delta === undefined) return null;
    if (delta === 0) return "según plan";
    if (delta > 0) return `${delta} d más tarde que el plan`;
    return `${-delta} d antes que el plan`;
  };
  const startText = describe(diff.startDeltaDays);
  if (startText) parts.push(`inicio real ${startText}`);
  const endText = describe(diff.endDeltaDays);
  if (endText) parts.push(`fin real ${endText}`);
  return parts.length > 0 ? parts.join("; ") : null;
}

export type RealCalendarEventResource = {
  installationId: number;
  clientName: string | null;
  realStartDateRaw: unknown;
  realEndDateRaw: unknown;
  inProgress: boolean;
  closureUnverified: boolean;
  realStatus: CalendarStatusPresentation;
  technicians: string[];
  totalRealMinutes: number | null;
  plannedStartLabel: string | null;
  plannedEndLabel: string | null;
  diffSummary: string | null;
  realSessionLabel: string;
  phaseId?: number;
  phaseTitle?: string;
  installationColor: string;
  style: {
    backgroundColor: string;
    borderLeft: string;
  };
};

/**
 * Builds "Real de obras" events strictly from explicit evidence:
 * - Installation summary plus one event per phase with explicit realStartDate.
 * - realEndDate missing → "En curso" through today only while installation remains open.
 *   A completed installation/phase without a real end is a one-day marker labelled
 *   as an unverified close; administrative status/history never supplies a date.
 * - Real activity rows (never mere assignments) contribute technician names and
 *   aggregated real minutes only — never dates. The labels distinguish legacy unlinked activity from explicit phase links.
 * - No temporal filter and no workday-state semantics are reused from Laboral.
 */
export function buildRealCalendarEvents(
  installations: Array<Record<string, unknown>> | null | undefined,
  realActivityRows: Array<Record<string, unknown>> | null | undefined,
  today: Date = new Date()
): Array<{
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: RealCalendarEventResource;
}> {
  const activityByInstallation = aggregateRealActivityByInstallation(realActivityRows);
  const events: Array<{
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    resource: RealCalendarEventResource;
  }> = [];

  (installations ?? []).forEach((inst: Record<string, unknown>) => {
    const installationIdForPhases = Number(inst.id);
    // Explicit phase dates and exact foreign-key links only. Never infer a phase
    // from overlapping planned dates, assignment dates, or workday status.
    for (const phase of (inst.planningSegments ?? []) as Array<Record<string, unknown>>) {
      const phaseId = Number(phase.id);
      if (!Number.isInteger(phaseId) || phaseId <= 0 || !phase.realStartDate) continue;
      if (phase.installationId != null && Number(phase.installationId) !== installationIdForPhases) continue;
      const linked = (realActivityRows ?? []).filter(row =>
        Number(row.installationId) === installationIdForPhases && row.planningSegmentId === phaseId);
      const phaseEvents = buildRealCalendarEvents([{ ...inst, planningSegments: [],
        realEvidenceScope: "phase",
        realStartDate: phase.realStartDate, realEndDate: phase.realEndDate }], linked, today);
      const event = phaseEvents[0];
      if (!event) continue;
      const diff = computeRealVsPlanningDiff({ planningSegments: [phase],
        realStartDate: phase.realStartDate, realEndDate: phase.realEndDate });
      event.id = `real-${installationIdForPhases}-phase-${phaseId}`;
      event.title = `${inst.clientName || "Instalación"} — ${phase.title} (Real)`;
      Object.assign(event.resource, { phaseId, phaseTitle: `${phase.title}${phase.deletedAt ? " (archivada)" : ""}`,
        plannedStartLabel: diff.plannedStart, plannedEndLabel: diff.plannedEnd,
        diffSummary: formatRealVsPlanningDiffSummary(diff),
        realSessionLabel: linked.some(isRealActivityRow) ? "Sesiones asociadas explícitamente a fase" : "Sin sesión real asociada",
      });
      events.push(event);
    }
    const realStart = parseCalendarTimestamp(inst.realStartDate);
    if (!realStart) return;

    const installationId = Number(inst.id);
    if (!Number.isInteger(installationId) || installationId <= 0) return;

    const realEnd = parseCalendarTimestamp(inst.realEndDate);
    const installationCompleted = inst.status === "completed";
    const inProgress = realEnd === null && !installationCompleted;
    const closureUnverified = realEnd === null && installationCompleted && realStart !== null;

    // Render span: start day → real end day (+1 day, exclusive, RBC allDay convention).
    // While in progress the evidence is rendered through today, without inventing an end date.
    const startDate = new Date(realStart.getFullYear(), realStart.getMonth(), realStart.getDate());
    let endDate: Date;
    if (realEnd) {
      endDate = new Date(realEnd.getFullYear(), realEnd.getMonth(), realEnd.getDate() + 1);
    } else if (inProgress) {
      endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    } else {
      // There is no verified end date. Keep a marker on the observed start day,
      // never a fabricated bar through today for a completed installation.
      endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1);
    }
    if (endDate.getTime() <= startDate.getTime()) {
      endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1);
    }

    const activity = activityByInstallation.get(installationId) ?? { technicians: [], totalMinutes: null };
    const diff = computeRealVsPlanningDiff({
      planningSegments: ((inst.planningSegments ?? []) as Array<{ startDate?: unknown; endDate?: unknown; deletedAt?: unknown }>).filter(phase => !phase.deletedAt),
      realStartDate: inst.realStartDate,
      realEndDate: inst.realEndDate,
    });
    const installationColor = getInstallationColor(installationId);

    events.push({
      id: `real-${installationId}`,
      title: `${(inst.clientName as string) || "Instalación"} (Real)`,
      start: startDate,
      end: endDate,
      allDay: true,
      resource: {
        installationId,
        clientName: (inst.clientName as string) || null,
        realStartDateRaw: inst.realStartDate,
        realEndDateRaw: inst.realEndDate,
        inProgress,
        realStatus: getRealStatusPresentation({
          realStartDate: inst.realStartDate,
          realEndDate: inst.realEndDate,
          installationStatus: inst.status,
          scope: inst.realEvidenceScope === "phase" ? "phase" : "installation",
        }),
        closureUnverified,
        technicians: activity.technicians,
        totalRealMinutes: activity.totalMinutes,
        plannedStartLabel: diff.plannedStart,
        plannedEndLabel: diff.plannedEnd,
        diffSummary: formatRealVsPlanningDiffSummary(diff),
        realSessionLabel: (realActivityRows ?? []).some(row => Number(row.installationId) === installationId && row.planningSegmentId != null && isRealActivityRow(row))
          ? ((realActivityRows ?? []).some(row => Number(row.installationId) === installationId && row.planningSegmentId == null && isRealActivityRow(row))
            ? "Resumen de obra · incluye sesiones sin fase" : "Resumen de obra · sesiones vinculadas a fases")
          : resolveRealSessionGapLabel(activity),
        installationColor,
        style: {
          backgroundColor: installationColor,
          borderLeft: inProgress ? "4px solid #1d4ed8" : closureUnverified ? "4px solid #6b7280" : "4px solid #047857",
        },
      },
    });
  });

  return events;
}

/**
 * Roles allowed to view and interact with the technician filter.
 * Includes admin, project_manager, and admin_manager (read-only filtering).
 */
export const ALLOWED_TECHNICIAN_FILTER_ROLES = [
  "admin",
  "project_manager",
  "admin_manager",
] as const;

export function canViewTechnicianFilter(role: string | null | undefined): boolean {
  if (!role) return false;
  return (ALLOWED_TECHNICIAN_FILTER_ROLES as readonly string[]).includes(role);
}

/**
 * Evaluates temporal visibility rules for execution calendar events.
 * - Past dates: only non-assigned (working, paused, completed) events are visible.
 * - Today: all events are visible.
 * - Future dates: only assigned events are visible.
 */
export function isExecutionEventVisible(
  eventDate: string | Date,
  status: string | null | undefined,
  referenceDate: string | Date = new Date()
): boolean {
  const evtDateStr =
    typeof eventDate === "string"
      ? eventDate.split("T")[0]
      : eventDate.toISOString().split("T")[0];

  const refDateStr =
    typeof referenceDate === "string"
      ? referenceDate.split("T")[0]
      : referenceDate.toISOString().split("T")[0];

  if (!evtDateStr) return false;

  if (evtDateStr < refDateStr) {
    return status !== "assigned";
  }
  if (evtDateStr > refDateStr) {
    return status === "assigned";
  }
  return true;
}

// ========== CAL-05: Laboral/Técnicos calendar (actual, plan and legacy separated) ==========

export function parseLaborClockTime(value: unknown, dateStr: string): Date | null {
  if (typeof value !== "string" || typeof dateStr !== "string") return null;
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? "0");
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  const parts = dateStr.split("T")[0].split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) return null;
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day, hours, minutes, seconds);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Formats a stored Europe/Madrid civil-clock marker without timezone conversion. */
export function formatLaborClockTime(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? "0");
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return `${String(hours).padStart(2, "0")}:${match[2]}`;
}

export type LaboralEventTimes = {
  start: Date;
  end: Date;
  hasRealTimes: boolean;
  realStartTime: string | null;
  realEndTime: string | null;
  realTimeLabel: string | null;
  allDay: boolean;
  kind: "actual" | "planned" | "unverified";
};

function laboralDayBounds(dateStr: string): { start: Date; end: Date } {
  const parts = dateStr.split("T")[0].split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) {
    return { start: new Date(0), end: new Date(0) };
  }
  const [year, month, day] = parts;
  const start = new Date(year, month - 1, day);
  if (start.getFullYear() !== year || start.getMonth() !== month - 1 || start.getDate() !== day) {
    return { start: new Date(0), end: new Date(0) };
  }
  return { start, end: new Date(year, month - 1, day + 1) };
}

/**
 * Assignment clock bounds can contain pauses, so render a civil-day marker and
 * show verified effective minutes separately; never infer a continuous block.
 */
export function resolveLaboralEventTimes(evt: {
  date: unknown;
  status?: unknown;
  totalMinutes?: unknown;
  startTime?: unknown;
  endTime?: unknown;
  activeStartTime?: unknown;
  notes?: string | null;
  shiftEndUnverified?: boolean;
}): LaboralEventTimes {
  const dateStr = typeof evt?.date === "string" ? evt.date : "";
  const day = laboralDayBounds(dateStr);
  const resolution = resolveLaborDayMinutes(evt as any);
  const kind = resolution.planned ? "planned" : resolution.durationVerified ? "actual" : "unverified";
  const realStartTime = formatLaborClockTime(evt.startTime);
  const realEndTime = formatLaborClockTime(evt.endTime);
  const realTimeLabel = realStartTime || realEndTime
    ? `Horas reales: ${[
      realStartTime ? `inicio ${realStartTime}` : null,
      realEndTime ? `fin ${realEndTime}` : null,
    ].filter(Boolean).join(" · ")}`
    : null;
  // Assignment clock markers can bound one or more active segments and pauses.
  // Display them separately; effective minutes remain the independent measure.
  return {
    ...day,
    hasRealTimes: Boolean(realStartTime || realEndTime),
    realStartTime,
    realEndTime,
    realTimeLabel,
    allDay: true,
    kind,
  };
}

/** Formats minutes as hours/minutes; this is labor time, never money. */
export function formatLaboralMinutes(totalMinutes: number | null | undefined): string {
  return formatLaborDurationMinutes(totalMinutes);
}

export type LaboralTechnicianMonthSummary = {
  technicianId: number;
  technicianName: string;
  /** Distinct days with verified activity; planning and legacy equivalence are separate. */
  days: number;
  assignments: number;
  totalMinutes: number | null;
  equivalentMinutes: number;
  plannedAssignments: number;
  plannedDays: number;
  unverifiedAssignments: number;
  installations: Array<{
    installationId: number;
    siteId: number | null;
    siteName: string | null;
    installationCode: string | null;
    workDescription: string | null;
    address: string | null;
    clientName: string | null;
    assignments: number;
    plannedAssignments: number;
    totalMinutes: number;
    equivalentMinutes: number;
  }>;
};

export function summarizeLaboralMonthByTechnician(
  rows: Array<Record<string, unknown>> | null | undefined,
  month: Date,
  now: Date = new Date(),
): LaboralTechnicianMonthSummary[] {
  if (!(month instanceof Date) || Number.isNaN(month.getTime())) return [];
  const prefix = String(month.getFullYear()) + "-" + String(month.getMonth() + 1).padStart(2, "0");
  type Entry = LaboralTechnicianMonthSummary & {
    actualDayKeys: Set<string>;
    plannedDayKeys: Set<string>;
    byInstallation: Map<number, LaboralTechnicianMonthSummary["installations"][number]>;
  };
  const byTechnician = new Map<number, Entry>();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const dateStr = typeof row?.date === "string" ? row.date : "";
    if (!dateStr.startsWith(prefix)) return;
    const technicianId = Number(row?.technicianId);
    if (!Number.isInteger(technicianId) || technicianId <= 0) return;
    const technicianName = typeof row?.technicianName === "string" && row.technicianName.trim()
      ? row.technicianName.trim()
      : "Técnico #" + technicianId;

    let entry = byTechnician.get(technicianId);
    if (!entry) {
      entry = {
        technicianId, technicianName, days: 0, assignments: 0, totalMinutes: null,
        equivalentMinutes: 0, plannedAssignments: 0, plannedDays: 0, unverifiedAssignments: 0,
        actualDayKeys: new Set(), plannedDayKeys: new Set(),
        installations: [], byInstallation: new Map(),
      };
      byTechnician.set(technicianId, entry);
    }
    const installationId = Number(row?.installationId);
    let installation = Number.isInteger(installationId) && installationId > 0
      ? entry.byInstallation.get(installationId)
      : undefined;
    if (!installation && Number.isInteger(installationId) && installationId > 0) {
      const clean = (value: unknown) => typeof value === "string" && value.trim() ? value.trim() : null;
      installation = {
        installationId,
        siteId: row.siteId == null ? null : Number(row.siteId),
        siteName: clean(row.siteName),
        installationCode: clean(row.installationCode),
        workDescription: clean(row.workDescription),
        address: clean(row.address),
        clientName: clean(row.clientName),
        assignments: 0,
        plannedAssignments: 0,
        totalMinutes: 0,
        equivalentMinutes: 0,
      };
      entry.byInstallation.set(installationId, installation);
    }
    const date = dateStr.split("T")[0];
    const resolution = resolveLaborDayMinutes(row as any, now);
    if (resolution.planned) {
      entry.plannedAssignments += 1;
      entry.plannedDayKeys.add(date);
      if (installation) {
        installation.plannedAssignments += 1;
        entry.installations = Array.from(entry.byInstallation.values()).sort((a, b) => a.installationId - b.installationId);
      }
      return;
    }
    if (resolution.durationVerified) {
      entry.assignments += 1;
      entry.actualDayKeys.add(date);
      entry.totalMinutes = (entry.totalMinutes ?? 0) + (resolution.minutes ?? 0);
      if (installation) {
        installation.assignments += 1;
        installation.totalMinutes += resolution.minutes ?? 0;
      }
    } else {
      entry.unverifiedAssignments += 1;
      entry.equivalentMinutes += resolution.equivalentMinutes;
      if (installation) installation.equivalentMinutes += resolution.equivalentMinutes;
    }
    if (installation) entry.installations = Array.from(entry.byInstallation.values()).sort((a, b) => a.installationId - b.installationId);
  });

  return Array.from(byTechnician.values())
    .map(({ actualDayKeys, plannedDayKeys, byInstallation: _byInstallation, ...summary }) => ({
      ...summary,
      days: actualDayKeys.size,
      plannedDays: plannedDayKeys.size,
    }))
    .sort((a, b) =>
      a.technicianName.localeCompare(b.technicianName, "es") || a.technicianId - b.technicianId);
}

export type DayBreadcrumbInfo = {
  rootLabel: string;
  monthLabel: string;
  dayLabel: string;
  backActionLabel: string;
};

/**
 * Generates formatted Spanish breadcrumb strings and action label for Day View.
 * Format: "Calendario > [Mes Año] > [Día] > Volver al mes"
 */
export function getDayViewBreadcrumbInfo(
  date: Date,
  locale = es
): DayBreadcrumbInfo {
  const rawMonth = format(date, "MMMM yyyy", { locale });
  const formattedMonth = rawMonth.charAt(0).toUpperCase() + rawMonth.slice(1);
  const dayFormatted = format(date, "d 'de' MMMM", { locale });

  return {
    rootLabel: "Calendario",
    monthLabel: formattedMonth,
    dayLabel: dayFormatted,
    backActionLabel: "Volver al mes",
  };
}

/**
 * Filters a collection of events for a specific target day.
 */
export function filterEventsForDate(events: any[], targetDate: Date): any[] {
  const targetYear = targetDate.getFullYear();
  const targetMonth = targetDate.getMonth();
  const targetDay = targetDate.getDate();
  const targetTime = new Date(targetYear, targetMonth, targetDay).getTime();

  return events.filter((evt) => {
    if (!evt || !evt.start) return false;
    const start = new Date(evt.start);
    const startYear = start.getFullYear();
    const startMonth = start.getMonth();
    const startDay = start.getDate();
    const startTime = new Date(startYear, startMonth, startDay).getTime();

    if (!evt.end) {
      return startTime === targetTime;
    }

    const end = new Date(evt.end);
    const endYear = end.getFullYear();
    const endMonth = end.getMonth();
    const endDay = end.getDate();
    let endTime = new Date(endYear, endMonth, endDay).getTime();

    // React Big Calendar stores allDay end as the next civil midnight.
    // Subtract a calendar date, not 24h, so Madrid DST days remain correct.
    if (evt.allDay && end.getHours() === 0 && end.getMinutes() === 0 && endTime > startTime) {
      endTime = new Date(endYear, endMonth, endDay - 1).getTime();
    }

    return targetTime >= startTime && targetTime <= endTime;
  });
}

// ========== CAL-09: Monthly labor summary (management-only, time only) ==========

/**
 * Roles allowed to open the monthly labor summary read model and its CSV
 * export: admin, project_manager, admin_manager. Technicians and sales never
 * receive this data from the UI (the server enforces the same gate).
 */
export const MONTHLY_LABOR_SUMMARY_ROLES = [
  "admin",
  "project_manager",
  "admin_manager",
] as const;

export function canViewMonthlyLaborSummary(role: string | null | undefined): boolean {
  if (!role) return false;
  return (MONTHLY_LABOR_SUMMARY_ROLES as readonly string[]).includes(role);
}

/** Spanish label for the explicit provenance of a workday's minutes. */
export function formatLaborProvenanceLabel(source: unknown): string {
  switch (source) {
    case "total_minutes":
      return "Minutos registrados";
    case "active_segment":
      return "Minutos registrados + tramo activo";
    case "unknown_duration":
      return "Duración no verificada";
    case "equivalent_day":
      return "Equivalencia histórica no verificada (08:00–17:00)";
    case "planned":
      return "Planificado; sin ejecución registrada";
    default:
      return "Sin procedencia";
  }
}

/** Percentage with exactly two decimals and the % sign ("76.17%"). */
export function formatLaborPercent(percent: number | null | undefined): string {
  const value = Number(percent);
  if (!Number.isFinite(value)) return "0.00%";
  return `${value.toFixed(2)}%`;
}

/** Canonical site identity with explicit legacy fallbacks. */
export function formatLaborInstallationIdentity(input: {
  installationId?: unknown;
  siteId?: unknown;
  siteName?: unknown;
  installationCode?: unknown;
  workDescription?: unknown;
  address?: unknown;
  clientName?: unknown;
}): { primary: string; secondary: string | null } {
  return resolveLaborInstallationIdentity(input);
}

/**
 * Reconciles the read model invariants for display and tests:
 * per technician, installation minutes add up to the monthly total and the
 * installation percentages add up to exactly 100 (integer-hundredths check,
 * immune to floating point noise).
 */
export function reconcileMonthlyLaborSummary(summary: {
  technicians: Array<{
    technicianId: number;
    totalMinutes: number;
    percentSum: number;
    installations: Array<{ totalMinutes: number }>;
  }>;
}): { ok: boolean; brokenTechnicianIds: number[] } {
  const brokenTechnicianIds: number[] = [];
  summary.technicians.forEach((tech) => {
    const installationMinutes = tech.installations.reduce(
      (sum, inst) => sum + inst.totalMinutes,
      0
    );
    const percentHundredths = Math.round(tech.percentSum * 100);
    const minutesReconcile = installationMinutes === tech.totalMinutes;
    const expectedPercent = tech.totalMinutes > 0 ? 10000 : 0;
    if (!minutesReconcile || percentHundredths !== expectedPercent) {
      brokenTechnicianIds.push(tech.technicianId);
    }
  });
  return { ok: brokenTechnicianIds.length === 0, brokenTechnicianIds };
}

/** File name for the monthly labor CSV export. */
export function monthlyLaborCsvFilename(month: string): string {
  return `resumen-mensual-mano-de-obra-${month}.csv`;
}

/**
 * Triggers a client-side CSV download. No-op outside a DOM (tests/node).
 */
export function triggerCsvDownload(filename: string, csv: string): void {
  if (typeof document === "undefined" || typeof URL === "undefined" || !URL.createObjectURL) return;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
